/* DocumentAttachZone — Noam Drive Phase 2 (P0)
 *
 * Composant réutilisable injecté dans la fiche détail de chaque module métier
 * (Risques, Pipeline, Courtage, Négo, Annuaire, Immobilier, Chaînage, Plans,
 * Audits, Incidents, Fournisseurs). Affiche les documents Noam Drive attachés
 * à l'entité courante (linked_to.module + linked_to.entity_id) et permet :
 *   - d'uploader un nouveau document directement attaché à cette entité
 *   - d'attacher un document existant du Drive
 *   - de détacher un document
 *   - de télécharger un document
 *
 * Props :
 *   - module        : clé du module cible (ex: "risques", "negociation"…)
 *   - entityId      : id de l'entité concrète
 *   - entityLabel   : libellé court affiché dans l'UI (optionnel)
 *   - compact       : variante compacte (sans titre, juste la liste + bouton)
 *
 * Mode strictement additif — n'écrase aucun champ existant de l'entité.
 */
import { useEffect, useRef, useState } from "react";
import { api, apiUpload } from "@/lib/api";
import {
  Paperclip, Upload, Loader2, X, Download, ExternalLink, Plus,
  FileText, AlertTriangle, Trash2,
} from "lucide-react";

function fmtSize(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function DocumentAttachZone({
  module,
  entityId,
  entityLabel,
  compact = false,
}) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const fileInput = useRef(null);

  const reload = async () => {
    if (!module || !entityId) { setLoading(false); return; }
    try {
      setLoading(true);
      const r = await api.get("/workspace/documents", {
        params: { linked_module: module, linked_entity_id: entityId, limit: 50 },
      });
      setDocs(r.data?.items || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Chargement échoué.");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [module, entityId]);

  const onUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      setErr("");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("module_source", module);
      fd.append("linked_module", module);
      fd.append("linked_entity_id", entityId);
      await apiUpload("/workspace/documents", fd);
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Upload échoué.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onDetach = async (docId) => {
    if (!window.confirm("Détacher ce document de la fiche ? Le fichier reste dans le Drive.")) return;
    try {
      await api.patch(`/workspace/documents/${docId}/link`, {
        module: null, entity_id: null,
      });
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Détachement échoué.");
    }
  };

  if (!module || !entityId) return null;

  return (
    <div
      className={compact ? "space-y-2" : "noam-card p-4 space-y-3"}
      data-testid={`doc-attach-zone-${module}`}
    >
      {!compact && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Paperclip className="w-3.5 h-3.5 text-[#378ADD]" />
            <div className="noam-overline">Documents attachés (Noam Drive)</div>
            {entityLabel && (
              <span className="text-[10px] text-zinc-500 truncate max-w-[180px]">· {entityLabel}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="btn-ghost text-[11px]"
              data-testid={`doc-attach-pick-${module}`}
            >
              <Plus className="w-3 h-3" /> Drive
            </button>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="btn-primary text-[11px] disabled:opacity-50"
              data-testid={`doc-attach-upload-${module}`}
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploading ? "Envoi…" : "Joindre"}
            </button>
            <input
              ref={fileInput}
              type="file"
              className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])}
            />
          </div>
        </div>
      )}

      {compact && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="btn-ghost text-[11px] disabled:opacity-50"
            data-testid={`doc-attach-upload-compact-${module}`}
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
            {uploading ? "Envoi…" : `Joindre un doc${docs.length ? ` (${docs.length})` : ""}`}
          </button>
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
        </div>
      )}

      {err && (
        <div className="flex items-start gap-2 p-2 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-[11px]">
          <AlertTriangle className="w-3 h-3 mt-0.5" /> <span>{String(err)}</span>
        </div>
      )}

      {loading ? (
        <div className="text-[11px] text-zinc-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
        </div>
      ) : docs.length === 0 ? (
        !compact && (
          <div className="text-[11px] text-zinc-500 italic">
            Aucun document attaché. Joignez un PDF, un mémo ou une image pour enrichir la fiche.
          </div>
        )
      ) : (
        <ul className="space-y-1" data-testid={`doc-attach-list-${module}`}>
          {docs.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] text-[11px]"
              data-testid={`doc-attach-item-${d.id}`}
            >
              <FileText className="w-3 h-3 text-[#378ADD] flex-shrink-0" />
              <span className="truncate flex-1 text-zinc-200" title={d.name}>{d.name}</span>
              <span className="text-zinc-500 text-[10px] font-mono">{fmtSize(d.size_bytes)}</span>
              <a
                href={`/api/workspace/documents/${d.id}/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-white"
                title="Télécharger"
                data-testid={`doc-attach-download-${d.id}`}
              >
                <Download className="w-3 h-3" />
              </a>
              <button
                type="button"
                onClick={() => onDetach(d.id)}
                className="text-zinc-400 hover:text-[#FCA5A5]"
                title="Détacher"
                data-testid={`doc-attach-detach-${d.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {showPicker && (
        <DrivePicker
          onClose={() => setShowPicker(false)}
          onPick={async (docId) => {
            try {
              await api.patch(`/workspace/documents/${docId}/link`, {
                module, entity_id: entityId,
              });
              setShowPicker(false);
              await reload();
            } catch (e) {
              setErr(e?.response?.data?.detail || e.message || "Attachement échoué.");
            }
          }}
        />
      )}
    </div>
  );
}

function DrivePicker({ onClose, onPick }) {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/workspace/documents", {
          params: q ? { search: q, limit: 100 } : { limit: 100 },
        });
        setDocs(r.data?.items || []);
      } finally { setLoading(false); }
    })();
  }, [q]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="doc-drive-picker"
    >
      <div
        className="noam-card max-w-2xl w-full p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="noam-overline">Noam Drive</div>
            <div className="text-sm font-semibold text-zinc-200">Choisir un document à attacher</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input
          autoFocus
          placeholder="Rechercher par nom, tag ou description…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="noam-input text-xs"
          data-testid="doc-drive-search"
        />
        <div className="max-h-[400px] overflow-y-auto space-y-1">
          {loading ? (
            <div className="text-[11px] text-zinc-500 flex items-center gap-1 p-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
            </div>
          ) : docs.length === 0 ? (
            <div className="text-[11px] text-zinc-500 italic p-2">Aucun document trouvé.</div>
          ) : (
            docs.map((d) => (
              <button
                key={d.id}
                onClick={() => onPick(d.id)}
                className="w-full text-left flex items-center gap-2 px-2 py-2 rounded-md border border-white/5 hover:border-[#378ADD]/40 hover:bg-[#378ADD]/5 text-[11px]"
                data-testid={`doc-drive-pick-${d.id}`}
              >
                <FileText className="w-3 h-3 text-[#378ADD]" />
                <span className="flex-1 truncate text-zinc-200">{d.name}</span>
                <span className="text-zinc-500 text-[10px] font-mono">{fmtSize(d.size_bytes)}</span>
                <ExternalLink className="w-3 h-3 text-zinc-500" />
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
